import { mdiAccountCheckOutline } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { useDashboardData } from './useDashboardData';

const MetricPaper = styled(Paper)`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 12px;
`;

const MetricTitle = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 20px auto 20px auto;
`;

const MetricTitleText = styled(Typography)`
    font-family: Lato;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.15000000596046448px;
    text-align: center;
    margin-left: 6px;
`;

const MetricTitleIcon = styled(Icon)`
    width: 20px;
`;

const AttendanceDataCircle = styled(CircularProgress)`
    z-index: 2;
`;

const GreyCircle = styled.div`
    width: 184px;
    height: 184px;

    border-radius: 50%;
    border: 15px solid #F3F3F3;

    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const CircleBox = styled(Box)`
    position: relative;
    display: inline-flex;
`;

const CircleDataBox = styled(Box)`
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    position: absolute;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const AmountText = styled(Typography)`
    display: block;
    font-family: Lato;
    font-size: 34px;
    font-style: normal;
    font-weight: 400;
    line-height: 42px;
    letter-spacing: 0.25px;
    text-align: center;
`;

const OutOfMaximumText = styled(Typography)`
    display: block;
    font-family: Lato;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.4000000059604645px;
    text-align: center;
`;

const AttendanceDataFooter = styled(Typography)`
    color: #56585F;
    margin: 10px auto 33px auto;
    font-family: Lato;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0.4000000059604645px;
    text-align: center;
`;

const DashboardAttendanceMetric: React.FC = () => {
    const { sessionSummary: summary, classAverages } = useDashboardData();
    const { t } = useTranslation('dashboard');
    return (
        <MetricPaper id='dashboard-metric-attendance'>
            <MetricTitle>
                <MetricTitleIcon path={mdiAccountCheckOutline}/>
                <MetricTitleText>
                    {t('attendees')}
                </MetricTitleText>
            </MetricTitle>
            <CircleBox>
                <AttendanceDataCircle
                    variant="determinate"
                    value={(summary.attendance.student / summary.maxAttendance) * 100}
                    size={184}
                    color='primary'
                />
                <GreyCircle/>
                <CircleDataBox data-testid='amount'>
                    <AmountText color='primary'>
                        {summary.attendance.student}
                    </AmountText>
                    <OutOfMaximumText>
                        {`${t('out-of')} ${summary.maxAttendance}`}
                    </OutOfMaximumText>
                </CircleDataBox>
            </CircleBox>
            <AttendanceDataFooter>
                {t('semester-average-full', { average: Math.round(classAverages?.attendance.avg ?? 0), max: classAverages?.attendance.max ?? 0 })}
            </AttendanceDataFooter>
        </MetricPaper>
    );
};

export default DashboardAttendanceMetric;
